import { Component, OnInit } from '@angular/core';
import { getInitials } from '@core/util/string';
import { UserProfile } from '@models/userProfile';
import { Select, Store } from '@ngxs/store';
import { BusinessAddressesService } from '@repos/businesses/business-addresses.service';
import { UploadService } from '@services/file/upload.service';
import { AuthState } from '@state/auth';
import { AuthDispatch } from '@state/auth/auth.dispatch';
import { LandscapePositions } from '@state/auth/auth.models';
import { UserProfileState } from '@state/user-profile';
import { UserProfileDispatch } from '@state/user-profile/dispatch';
import { CreateStatusType } from '@state/utils/models';
import { Observable } from 'rxjs';
import { filter, pairwise, switchMap, take } from 'rxjs/operators';
import { SnackbarService } from 'src/app/shared/services/ui/snackbar.service';
import { UrlService } from 'src/app/shared/services/url/url.service';
import { BusinessService } from '../../../../shared/api-repos/business/business.api.service';
import { BusinessInformationFormData } from './create-user-business/create-user-business.component';
import { UserProfileForm } from './create-user-profile/create-user-profile.component';
import { Logger } from '@core/logger';
import { Location } from '@angular/common';
import { BusinessesSignUpRequestsRepo } from '@repos/signupRequests/signUpRequests.repo';
import {
	BusinessSignUpRequestModel,
	BusinessSignupRequestStatutes,
} from '@models/businessSignUpRequests';
import { NavigationService } from '@services/nav/nav.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import {
	BusinessSettingsVisibleTemplates,
	MatterDocumentFlows,
} from '@models/documents';
import { BusinessState } from '@state/business';

export enum CreateUserStepStates {
	PROFILE = 0,
	BUSINESS = 1,
	INVITE = 2,
}

type ISteps<T extends CreateUserStepStates = CreateUserStepStates> = {
	heading: string;
	stepName: T;
	subHeading: string;
	paragraph?: string;
	list?: string[];
	onSubmit: (formData?: unknown) => void;
};
type IStepHeadings<T extends CreateUserStepStates = CreateUserStepStates> =
	Record<T, ISteps<T>>;

@UntilDestroy()
@Component({
	selector: 'app-create-user',
	templateUrl: './create-user.component.html',
	styleUrls: ['./create-user.component.scss'],
	standalone: false,
})
export class CreateUserComponent implements OnInit {
	public createStatusTypes = CreateStatusType;
	public stepHeadings: IStepHeadings = {
		[CreateUserStepStates.PROFILE]: {
			heading: 'Welcome To Closer',
			stepName: CreateUserStepStates.PROFILE,
			subHeading: 'To finish setup, fill in the following information.',
			list: [
				'Personal information',
				'Business information',
				'Invite your colleagues (optional)',
			],
			onSubmit: (formData: UserProfileForm) => {
				this.createOrUpdateProfile(formData, !!this.userProfile?.firstName);

				this.createStatus$
					.pipe(
						filter((status) => status === CreateStatusType.success),
						take(1),
					)
					.subscribe(() => this.nextStep(1));
			},
		},
		[CreateUserStepStates.BUSINESS]: {
			heading: 'Confirm Business Details',
			stepName: CreateUserStepStates.BUSINESS,
			subHeading: 'Did we get it right?',
			paragraph:
				"Double-check your firm's name and address before we finalize the set up of your new Closer account.",
			onSubmit: async (
				formData: BusinessInformationFormData,
			): Promise<void> => {
				const request = this.createdBusinessFormData
					? this.updateBusiness
					: this.submitBusiness;
				await request(formData);
			},
		},
		[CreateUserStepStates.INVITE]: {
			heading: 'Start Collaborating',
			stepName: CreateUserStepStates.INVITE,
			subHeading:
				'Why wait? Invite your colleagues now, so you can beginning exploring Closer as a team.',
			onSubmit: () => this.nextStep(),
		},
	};
	public states = CreateUserStepStates;
	public currentStepIndex = 0;
	public steps: ISteps[] = [];
	public get currentStep() {
		return this.steps[this.currentStepIndex];
	}
	public get businessName(): string {
		return this.createdBusinessFormData?.businessName;
	}
	public creatingBusiness = false;
	public createdUserFormData: UserProfileForm;
	public createdBusinessFormData: BusinessInformationFormData;
	private addressId: string;

	@Select(AuthState.getEmail) loggedInUserEmail$: Observable<string>;
	@Select(AuthState.getLoading) authLoading$: Observable<boolean>;
	@Select(UserProfileState.getCreateStatus)
	createStatus$: Observable<CreateStatusType>;
	@Select(AuthState.getLandscapePosition)
	landscapePosition$: Observable<number>;
	@SelectSnapshot(AuthState.getSignUpRequest)
	signUpRequest: BusinessSignUpRequestModel;
	@SelectSnapshot(BusinessState.newMatterDocumentFlows)
	matterDocumentFlows: MatterDocumentFlows;

	constructor(
		private userProfileDispatch: UserProfileDispatch,
		private authDispatch: AuthDispatch,
		private snackBarService: SnackbarService,
		private businessAddressesService: BusinessAddressesService,
		private uploadService: UploadService,
		private businessService: BusinessService,
		private store: Store,
		private location: Location,
		private businessesSignUpRequestsRepo: BusinessesSignUpRequestsRepo,
		private navService: NavigationService,
	) {}

	get contactUrl(): string {
		return UrlService.Contact();
	}

	get legalUrl(): string {
		return UrlService.Legal();
	}

	get createdByName(): string {
		return [this.userProfile.firstName, this.userProfile.lastName]
			.join(' ')
			.trim();
	}

	ngOnInit(): void {
		this.authDispatch.SetLandscapePosition(LandscapePositions.DEFAULT);

		const userProfileLoaded = this.store
			.select(UserProfileState.getLoaded)
			.pipe(
				filter((loaded) => loaded),
				take(1),
			);

		/**
		 * To handle other tabs.
		 */

		this.store
			.select(UserProfileState.getCurrentBusinessId)
			.pipe(
				untilDestroyed(this),
				pairwise(),
				filter(
					([prev, curr]) => prev !== curr && curr && !this.creatingBusiness,
				),
			)
			.subscribe(() => {
				this.navService.goToDashboard();
			});

		// This would only occur for socials if they did not click on the link in the emails
		// because their account technically already exists
		userProfileLoaded
			.pipe(
				filter(() => {
					return (
						!this.userProfile.currentBusinessId &&
						!this.store.selectSnapshot(AuthState.getSignUpRequest)
					);
				}),
				switchMap(() => {
					return this.businessesSignUpRequestsRepo.GetRequestsByEmailAndStatus(
						this.store.selectSnapshot(AuthState.getEmail),
						BusinessSignupRequestStatutes.APPROVED,
					);
				}),
				filter((requests) => {
					return !!requests?.length;
				}),
				take(1),
			)
			.subscribe((requests) => {
				this.authDispatch.SetSignUpRequest(requests[0]);
			});

		userProfileLoaded.subscribe(() => {
			if (
				!this.userProfile.multiBusiness &&
				this.userProfile.currentBusinessId
			) {
				this.stepHeadings[CreateUserStepStates.PROFILE].list = [];
				this.stepHeadings[CreateUserStepStates.PROFILE].subHeading =
					'Almost there! Let’s make it official with your name, initials, and role within your firm.';
				this.steps = [this.stepHeadings[CreateUserStepStates.PROFILE]];
			} else if (this.userProfile.firstName) {
				this.steps = [
					this.stepHeadings[CreateUserStepStates.BUSINESS],
					this.stepHeadings[CreateUserStepStates.INVITE],
				];
			} else {
				this.steps = [
					this.stepHeadings[CreateUserStepStates.PROFILE],
					this.stepHeadings[CreateUserStepStates.BUSINESS],
					this.stepHeadings[CreateUserStepStates.INVITE],
				];
			}

			if (
				this.steps.some(
					(step) => step.stepName === CreateUserStepStates.PROFILE,
				) &&
				localStorage.getItem('loginDisplayName')
			) {
				const displayName = localStorage.getItem('loginDisplayName');
				const [firstName, lastName] = displayName.split(' ');
				this.createdUserFormData = {
					firstName,
					lastName,
					initials: getInitials(displayName),
					role: null,
					phoneNumber: '',
				};
			}
		});
	}

	public goBack(): void {
		if (this.currentStepIndex === 0) {
			this.location.back();
		} else {
			this.currentStepIndex--;
		}
	}

	private createOrUpdateProfile(
		formData: UserProfileForm,
		create = true,
	): void {
		const { uid, email } = this.store.selectSnapshot(AuthState.getLoggedInUser);
		const userFormData = {
			id: uid,
			email,
			firstName: formData.firstName,
			lastName: formData.lastName,
			initials: formData.initials,
			role: formData.role,
			currentBusinessId:
				this.store.selectSnapshot(UserProfileState.getCurrentBusinessId) ??
				null,
			...(formData.phoneNumber && { phoneNumber: formData.phoneNumber }),
			...(create && { teranetUsername: '' }), // legacy code no idea why it's like this
		};

		this.userProfileDispatch.CreateOrMerge(userFormData);

		localStorage.removeItem('loginDisplayName');
		this.createdUserFormData = formData;
	}

	private submitBusiness = (
		formData: BusinessInformationFormData,
	): Promise<void> =>
		this.businessErrorHandler('create', async () => {
			const newMatterDocumentFlows =
				this.signUpRequest?.newMatterDocumentFlows ?? this.matterDocumentFlows;

			const visibleTemplates =
				newMatterDocumentFlows === MatterDocumentFlows.LEAP
					? BusinessSettingsVisibleTemplates.WORD
					: newMatterDocumentFlows;

			const { businessId, addressId } = await this.businessService
				.create(
					formData.businessName,
					{ ...formData.businessAddress },
					newMatterDocumentFlows,
					visibleTemplates as unknown as BusinessSettingsVisibleTemplates,
					this.signUpRequest?.tags,
				)
				.toPromise();

			this.addressId = addressId;
			if (
				newMatterDocumentFlows === MatterDocumentFlows.HTML &&
				formData?.letterheadFile
			) {
				this.uploadService.uploadLetterhead(
					{
						name: formData.businessName,
						id: businessId,
						letterhead: formData.letterheadFile,
					},
					this.userProfile,
				);
			}

			return formData;
		});

	private updateBusiness = (
		formData: BusinessInformationFormData,
	): Promise<void> =>
		this.businessErrorHandler('update', async () => {
			await this.businessAddressesService.Update(this.addressId, {
				...formData.businessAddress,
				officeName: formData.businessName,
			});
			return formData;
		});

	private async businessErrorHandler(
		action: 'create' | 'update',
		cb: () => Promise<BusinessInformationFormData>,
	): Promise<void> {
		try {
			this.creatingBusiness = true;
			this.createdBusinessFormData = await cb();
			this.nextStep();
		} catch (err) {
			const userId = this.store.selectSnapshot(AuthState.getUserId);
			Logger.exception(err as Error, {
				message: `Unable to ${action} new business for user ${userId}`,
			});
			this.snackBarService.openSnackBar({
				text: `Unable to ${action} business. Try logging in again and retrying.`,
				type: 'error',
			});
		} finally {
			this.creatingBusiness = false;
		}
	}

	private get userProfile(): UserProfile {
		return this.store.selectSnapshot(UserProfileState.getProfile);
	}

	private nextStep(index?: number): void {
		const nextIndex = index > -1 ? index : this.currentStepIndex + 1;
		if (!this.steps[nextIndex]) {
			this.navService.goToDashboard();
		} else {
			this.currentStepIndex = nextIndex;
		}
	}
}
